import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, ListPlus, AlertTriangle } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import {
  getOrderedWeekDays, getMealSlots, longDay,
  getMealSlotByDayAndSlot, isSlotEmpty,
} from "../utils/mealPlanUtils";

/* ════════════════════════════════════════════════════════════════════════
   RECIPE → MEAL SLOT MODAL
   Mobile-friendly fallback for "Add to meal plan" on each recipe card.
   Lets the user pick the day + meal slot + optional cook, with replace
   confirmation when the chosen slot already has a meal.
   ════════════════════════════════════════════════════════════════════════ */

export function RecipeAddToSlotModal({ open, recipe, onClose }) {
  const {
    weeklyMealPlan, mealPlanSettings, members,
    setMealSlotFromRecipe,
  } = useHomeOSState();

  const orderedDays = useMemo(
    () => getOrderedWeekDays(mealPlanSettings?.weekStartDay || "Fri"),
    [mealPlanSettings?.weekStartDay]
  );
  const slotsCfg = useMemo(() => getMealSlots(mealPlanSettings), [mealPlanSettings]);

  const [day, setDay] = useState(orderedDays[0]);
  const [slotId, setSlotId] = useState(slotsCfg.find(s => s.id === "dinner")?.id || slotsCfg[0]?.id);
  const [cook, setCook] = useState("");
  const [keepCook, setKeepCook] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDay(orderedDays[0]);
    setSlotId(slotsCfg.find(s => s.id === "dinner")?.id || slotsCfg[0]?.id);
    setCook(recipe?.assignedDefaultCook || "");
    setKeepCook(false);
  }, [open, orderedDays, slotsCfg, recipe]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !recipe) return null;

  const existing = getMealSlotByDayAndSlot(weeklyMealPlan || [], day, slotId);
  const willReplace = !isSlotEmpty(existing);
  const slotLabel = slotsCfg.find(s => s.id === slotId)?.label || slotId;
  const existingCook = existing?.assignedCook || null;

  const confirm = () => {
    if (willReplace) {
      const ok = typeof window === "undefined"
        ? true
        : window.confirm(`Replace existing meal in ${longDay(day)} ${slotLabel.toLowerCase()}?`);
      if (!ok) return;
    }
    const recipeForApply = cook ? { ...recipe, assignedDefaultCook: cook } : recipe;
    setMealSlotFromRecipe(day, slotId, recipeForApply, {
      keepCook: keepCook && willReplace,
      existingCook,
    });
    toast.success(
      willReplace
        ? `${longDay(day)} ${slotLabel.toLowerCase()} replaced with ${recipe.title}.`
        : `${recipe.title} added to ${longDay(day)} ${slotLabel.toLowerCase()}.`
    );
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="recipe-add-to-slot-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-3xl bg-white dark:bg-slate-900 shadow-none border border-[color:var(--ara-line-soft)] dark:border-rose-900/40 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start gap-3     dark: dark: dark:">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white dark:bg-slate-800 shadow-sm flex-shrink-0">
            {recipe.imageUrl ? (
              <img src={recipe.imageUrl} alt={recipe.title} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl" aria-hidden>{recipe.imageEmoji || "🍽️"}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Add to meal plan</p>
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight truncate" data-testid="recipe-add-to-slot-title">{recipe.title}</h2>
            <p className="text-[11px] font-bold text-gray-500 mt-0.5">Choose where this recipe should land.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center text-gray-400"
            aria-label="Close"
            data-testid="recipe-add-to-slot-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <Field label="Day">
            <select
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className={INPUT_CLS}
              data-testid="recipe-add-to-slot-day"
            >
              {orderedDays.map(d => <option key={d} value={d}>{longDay(d)}</option>)}
            </select>
          </Field>
          <Field label="Meal slot">
            <select
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              className={INPUT_CLS}
              data-testid="recipe-add-to-slot-slot"
            >
              {slotsCfg.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Assigned cook (optional)">
            <select
              value={cook}
              onChange={(e) => setCook(e.target.value)}
              className={INPUT_CLS}
              data-testid="recipe-add-to-slot-cook"
            >
              <option value="">Unassigned</option>
              <option value="all">Everyone</option>
              {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>

          {willReplace && (
            <div className="rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-amber-800 bg-transparent dark:bg-transparent px-3 py-2.5" data-testid="recipe-add-to-slot-replace-warning">
              <p className="text-xs font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> This slot already has a meal: <span className="truncate">{existing?.title || "Untitled meal"}</span>
              </p>
              <label className="flex items-center gap-2 mt-2 text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">
                <input
                  type="checkbox"
                  checked={keepCook}
                  onChange={(e) => setKeepCook(e.target.checked)}
                  data-testid="recipe-add-to-slot-keep-cook"
                />
                Keep the existing assigned cook
              </label>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-1 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-extrabold text-gray-700 dark:text-gray-200"
            data-testid="recipe-add-to-slot-cancel"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-transparent hover:opacity-80 text-white text-sm font-extrabold shadow-sm active:scale-[0.98]"
            data-testid="recipe-add-to-slot-confirm"
          >
            <ListPlus className="w-4 h-4" strokeWidth={1.5} /> {willReplace ? "Replace meal" : "Add to plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

const INPUT_CLS = "w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default RecipeAddToSlotModal;
